import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Optional, Output, Renderer2, Self, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import {
  MAT_FORM_FIELD, MatFormField,
  MatFormFieldControl
} from '@angular/material/form-field';
import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js-custom'
import { Subject } from 'rxjs';

import { digitsOnly, isNilOrEmpty } from 'helpers';
import { parse } from 'libphonenumber-js';
import { getPhonePlaceholder, isInternationalCountry } from 'core/geography';

/** Custom `MatFormFieldControl` for telephone number input. */
@Component({
  selector: 'app-mat-phone-input',
  templateUrl: 'mat-phone-input.component.html',
  styleUrls: ['mat-phone-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: MatPhoneInputComponent }],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    // @JARROD: This class gets applied to the input when label is floating.
    // I also made a few changes in _inputs.scss to push the label to the right of the icon
    // &.mat-form-field-type-phone-input :not(.mat-form-field-should-float) {
    '[class.example-floating]': 'shouldLabelFloat',
    '[id]': 'id',
  }
})
export class MatPhoneInputComponent
  implements ControlValueAccessor, MatFormFieldControl<string>, OnDestroy {
  static nextId = 0;
  @Input() campaignCountry: string = 'US';

  get defaultCountry() {
    return this.campaignCountry || 'US';
  }

  @ViewChild('phoneInput') phoneInput: HTMLInputElement;

  @Output() countryCodeEvent = new EventEmitter<string>();

  // CORE UNDERYLING VALUE
  private _e164Phone: string;
  // Value displayed to the user
  displayValueControl: UntypedFormControl = new UntypedFormControl();

  stateChanges = new Subject<void>();
  focused = false;
  // This gets added as a class name 'mat-form-field-type-phone-input'
  controlType = 'phone-input';
  id = `phone-input-+${MatPhoneInputComponent.nextId++}`;
  onChange = (_: any) => { };
  onTouched = () => { };

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty() {
    return isNilOrEmpty(this.displayValueControl.value);
  }

  get countryCode() {
    return parsePhoneNumber(this._e164Phone || '')?.country || this.defaultCountry;
  }

  get flagIcon() {
    return 'flag-icon-' + this.countryCode?.toLowerCase();
  }

  get defaultPlaceholder() {
    return getPhonePlaceholder(this.countryCode);
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') userAriaDescribedBy: string;

  @Input()
  get placeholder(): string {
    return this._placeholder || this.defaultPlaceholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.displayValueControl.disable() : this.displayValueControl.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value() {
    return this._e164Phone;
  }

  set value(value: string | null) {
    this._e164Phone = parsePhoneNumber(
      value || '', !value?.includes('+') ? this.defaultCountry : null
    )?.format('E.164');
    this.setDisplayValue(value || '');
    this.onChange(this.value);
    setTimeout(() => {
      this.countryCodeEvent.emit(this.countryCode);
    })
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return !!(this.displayValueControl.dirty && this.ngControl?.invalid);
  }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private _renderer: Renderer2,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl) {

    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.displayValueControl.valueChanges
      .subscribe((v) => {
        this.value = v;
      });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement
      .querySelector('.phone-input-container');
    controlElement?.setAttribute('aria-describedby', ids.join(' '));
  }

  private setDisplayValue(e164PhoneFormatted: string) {
    const pn = parsePhoneNumber(e164PhoneFormatted || '');
    if (isInternationalCountry(this.defaultCountry)) {
      this.displayValueControl.setValue(
        pn?.countryCallingCode === getCountryCallingCode(this.defaultCountry) ? pn.formatNational() : e164PhoneFormatted, {
        emitEvent: false
      });
      return;
    }
    const displayValue = pn?.countryCallingCode === '1' ? pn.formatNational() : e164PhoneFormatted;
    this.displayValueControl.setValue(displayValue, {
      emitEvent: false
    });
  }

  writeValue(phone: string): void {
    // In theory all phone numbers will start with + now. This was for backwards compatibility
    if (!isNilOrEmpty(phone) && !phone?.startsWith('+') && digitsOnly(phone)?.length === 10) {
      phone = `+1${phone}`;
    }
    this.value = phone;
    setTimeout(() => this.onChange(this.value));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onContainerClick() {
    this._focusMonitor.focusVia(this.phoneInput, 'program');
  }

  /**
   * When input is blurred, format the number
   */
  onInputBlurred() {
    const pn = parsePhoneNumber(this._e164Phone || '');
    if (!pn?.isValid) {
      return;
    }

    this.setDisplayValue(pn.format('E.164'));
  }

  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_required: BooleanInput;
}
